import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  Dialog,
  DialogBackdrop,
  DialogPanel
} from '@headlessui/react'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { navigate } from 'gatsby'
import React, { useEffect, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useAlgoliaSearch } from '../../../hooks/useAlgoliaSearch'
import { useUIStore } from '../../../stores/ui/uiStore'
import { EmptyResponse } from './EmptyResponse'
import { HeadingListResult } from './HeadingListResult'
import { ItemResult } from './ItemResult'
import { QuickActions } from './QuickActions'
import { RecentSearches } from './RecentSearches'

interface BaseCommandePalettesProps {
  isOpen: boolean
  algoliaConfig: {
    appId: string
    searchkey: string
    offersIndex: string
    firmsIndex: string
  }
}

export const BaseCommandePalettes = ({ isOpen = false, algoliaConfig }: BaseCommandePalettesProps) => {
  const [query, setQuery] = useState('')
  const closeCommandPalettes = useUIStore(useShallow(state => state.closeCommandPalettes))

  // Initialize Algolia search if config is provided
  const {
    results: searchResults = [],
    offers: offersResults = [],
    firms: firmsResults = [],
    search: performSearch,
    clearResults: clearSearchResults,
  } = useAlgoliaSearch({
    appId: algoliaConfig?.appId || '',
    searchkey: algoliaConfig?.searchkey || '',
    offersIndex: algoliaConfig?.offersIndex || '',
    firmsIndex: algoliaConfig?.firmsIndex || '',
    debounceMs: 300,
  })

  // Perform search when query changes
  useEffect(() => {
    performSearch(query)
  }, [query])

  // Clear search results when dialog closes
  useEffect(() => {
    if (!isOpen) {
      clearSearchResults()
      setQuery('')
    }
  }, [isOpen, clearSearchResults])

  return (
    <Dialog
      className="relative z-10"
      open={isOpen}
      onClose={() => {
        closeCommandPalettes()
        setQuery('')
      }}
    >
      <DialogBackdrop
        transition
        data-testid="dialog-backdrop"
        className="fixed inset-0 transition-opacity bg-gray-500/40 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />

      <div className="overflow-y-auto fixed inset-0 z-10 p-4 w-screen sm:p-6 md:p-20 md:pt-[136px]">
        <DialogPanel
          transition
          className="overflow-hidden mx-auto max-w-3xl rounded-xl shadow-2xl backdrop-filter backdrop-blur-sm transition-all transform bg-white/80 data-closed:scale-95 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
        >
          <Combobox
            onChange={(item) => {
              if (item) {
                // Naviguer vers l'URL de l'item sélectionné
                navigate(item as string)
                // Fermer la commande palette
                closeCommandPalettes()
                // Réinitialiser la requête
                setQuery('')
              }
            }}
          >
            <div className="grid grid-cols-1">
              <ComboboxInput
                autoFocus
                className="col-start-1 row-start-1 pr-4 pl-11 w-full h-12 text-base rounded-t-xl text-night !outline-hidden placeholder:text-gray-400 sm:text-sm !focus:outline-none !focus:ring-0 !focus-visible:ring-0 !focus:border-none !border-none"
                placeholder="Rechercher un cabinet ou une offre..."
                onChange={(event) => setQuery(event.target.value)}
              />
              <MagnifyingGlassIcon
                className="col-start-1 row-start-1 self-center ml-4 pointer-events-none text-night size-5"
                aria-hidden="true"
              />
            </div>

            {(query === '' || searchResults.length > 0) && (
              <ComboboxOptions static as="ul" className="overflow-y-auto max-h-[70vh] divide-y divide-gray-100 scroll-py-2">
                <RecentSearches
                  recentSearches={[]}
                  query={query}
                />

                {query !== '' && searchResults.length > 0 && (
                  <li>
                    <h2 className="px-3 mt-4 mb-2 text-xs font-semibold text-gray-500">Résultats de recherche</h2>
                    {offersResults.length > 0 && (
                      <HeadingListResult type="offers" query={query} />
                    )}

                    <ul className="text-sm text-gray-700">
                      {offersResults.map((result) => (
                        <ItemResult
                          key={result.objectID}
                          result={result}
                        />
                      ))}
                    </ul>

                    {firmsResults.length > 0 && (
                      <HeadingListResult type="firms" query={query} />
                    )}

                    <ul className="text-sm text-gray-700">
                      {firmsResults.map((result) => (
                        <ItemResult
                          key={result.objectID}
                          result={result}
                        />
                      ))}
                    </ul>
                  </li>
                )}
                <QuickActions query={query} />
              </ComboboxOptions>
            )}

            <EmptyResponse isVisible={query !== '' && searchResults.length === 0} />
          </Combobox>
        </DialogPanel>
      </div>
    </Dialog>
  )
}
